import { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Save, RotateCcw, X, Beaker, Droplets, FlaskConical,
  Waves, Gauge, Thermometer, Atom, ThermometerSun, ShieldAlert,
  Wind, Box, Layers, Filter, Trash2, ArrowRight, CheckCircle2,
  AlertTriangle, Info, TrendingUp, BarChart3, Zap, CircleDot,
  Factory, Milk, FishSymbol,
} from 'lucide-react';

import { useSession } from '@/context/SessionContext';
import { getStandard } from '@/types/standards';

/* ─── Types ─── */
interface NewSampleForm {
  customerId: string;
  sampleName: string;
  flowRate: number;
  bod: number;
  cod: number;
  tss: number;
  ph: number;
  nh3: number;
  og: number;
  lead: number;
  chromium: number;
  tds: number;
  conductivity: number;
  temperature: number;
  notes: string;
  standard: string;
  assessAfterSave: boolean;
}

/* ─── Parameter config ─── */
const PARAM_CONFIG = [
  { key: 'flowRate', label: 'Flow Rate', unit: 'm\u00B3/day', min: 10, max: 2000, step: 5, icon: Gauge, color: '#06b6d4', desc: 'Daily wastewater volume' },
  { key: 'bod', label: 'BOD\u2085', unit: 'mg/L', min: 0, max: 10000, step: 10, icon: Droplets, color: '#38bdf8', desc: 'Biochemical Oxygen Demand' },
  { key: 'cod', label: 'COD', unit: 'mg/L', min: 0, max: 15000, step: 10, icon: FlaskConical, color: '#f59e0b', desc: 'Chemical Oxygen Demand' },
  { key: 'tss', label: 'TSS', unit: 'mg/L', min: 0, max: 5000, step: 10, icon: Filter, color: '#8b5cf6', desc: 'Total Suspended Solids' },
  { key: 'ph', label: 'pH', unit: '', min: 3.0, max: 11.0, step: 0.1, icon: Beaker, color: '#10b981', desc: 'Acidity / Alkalinity' },
  { key: 'nh3', label: 'NH\u2083-N', unit: 'mg/L', min: 0, max: 300, step: 1, icon: Atom, color: '#06b6d4', desc: 'Ammonia Nitrogen' },
  { key: 'og', label: 'O&G', unit: 'mg/L', min: 0, max: 200, step: 1, icon: ThermometerSun, color: '#d97706', desc: 'Oil & Grease' },
  { key: 'lead', label: 'Lead', unit: 'mg/L', min: 0, max: 10, step: 0.01, icon: ShieldAlert, color: '#ef4444', desc: 'Heavy metal' },
  { key: 'chromium', label: 'Chromium', unit: 'mg/L', min: 0, max: 10, step: 0.01, icon: ShieldAlert, color: '#ef4444', desc: 'Heavy metal' },
  { key: 'tds', label: 'TDS', unit: 'mg/L', min: 0, max: 5000, step: 50, icon: Layers, color: 'var(--rb-text-3)', desc: 'Total Dissolved Solids' },
  { key: 'conductivity', label: 'Conductivity', unit: '\u00B5S/cm', min: 0, max: 10000, step: 100, icon: Zap, color: 'var(--rb-text-3)', desc: 'Electrical conductivity' },
  { key: 'temperature', label: 'Temperature', unit: '\u00B0C', min: 5, max: 40, step: 0.5, icon: Thermometer, color: 'var(--rb-text-3)', desc: 'Sample temperature' },
];

/* ─── Profiles ─── */
const PROFILES = [
  {
    name: 'Dairy Effluent',
    description: 'High organic, moderate FOG, neutral pH',
    icon: Milk,
    defaults: { flowRate: 250, bod: 1850, cod: 3200, tss: 450, ph: 6.8, nh3: 85, og: 12, lead: 0.3, chromium: 0.1, tds: 1200, conductivity: 1500, temperature: 25, standard: 'UK_EA' },
  },
  {
    name: 'Brewery Effluent',
    description: 'Very high COD/BOD, acidic, high TSS',
    icon: Factory,
    defaults: { flowRate: 350, bod: 2200, cod: 4100, tss: 620, ph: 5.2, nh3: 110, og: 18, lead: 0.2, chromium: 0.1, tds: 900, conductivity: 1100, temperature: 20, standard: 'UK_EA' },
  },
  {
    name: 'Fish Processing',
    description: 'High TSS, blood/oil, moderate COD',
    icon: FishSymbol,
    defaults: { flowRate: 150, bod: 1450, cod: 2650, tss: 520, ph: 6.9, nh3: 95, og: 22, lead: 0.1, chromium: 0.05, tds: 1100, conductivity: 1400, temperature: 15, standard: 'UK_EA' },
  },
];

/* ─── Preset wastewater types for quick entry ─── */
const WASTE_TYPES = [
  { label: 'Dairy', profileIndex: 0 },
  { label: 'Brewery', profileIndex: 1 },
  { label: 'Fish Processing', profileIndex: 2 },
  { label: 'Custom', profileIndex: -1 },
];

/* ─── Helper: generate process train from a single sample ─── */
function generateProcessTrainFromSample(params: Record<string, number>): { process: string; purpose: string; reason: string }[] {
  const bod = params.bod || 0;
  const cod = params.cod || 0;
  const tss = params.tss || 0;
  const og = params.oandg || params.og || 0;
  const nh3 = params.nh3 || 0;
  const ph = params.ph || 7;
  const lead = params.lead || 0;
  const chromium = params.chromium || 0;
  const hasMetals = lead > 0.2 || chromium > 0.5;

  const train: { process: string; purpose: string; reason: string }[] = [];

  if (tss > 100 || og > 20) {
    train.push({ process: 'DAF', purpose: 'Remove solids, fats, oils', reason: `High TSS (${Math.round(tss)} mg/L) and/or O&G (${Math.round(og)} mg/L)` });
  }
  if (tss > 50 && tss <= 500) {
    train.push({ process: 'Lamella Clarifier', purpose: 'Settle suspended solids', reason: `TSS ${Math.round(tss)} mg/L — inclined plate settling` });
  }
  if (ph < 6.0 || ph > 9.0) {
    train.push({ process: 'pH Adjustment', purpose: 'Neutralisation', reason: `pH ${ph.toFixed(1)} outside 6-9 range` });
  }
  if (cod > 500 || bod > 300) {
    train.push({ process: 'Aeration / MBBR', purpose: 'Biological oxidation', reason: `High organic load: COD ${Math.round(cod)} mg/L` });
    train.push({ process: 'Secondary Clarifier', purpose: 'Biomass separation', reason: 'Required after biological treatment' });
  }
  if (nh3 > 5) {
    train.push({ process: 'Nitrification', purpose: 'Ammonia removal', reason: `Elevated NH₃-N: ${Math.round(nh3)} mg/L` });
  }
  if (hasMetals) {
    train.push({ process: 'Chemical Precipitation', purpose: 'Heavy metals removal', reason: 'Elevated heavy metals detected' });
  }
  if (cod > 100 || tss > 30) {
    train.push({ process: 'Multimedia Filter', purpose: 'TSS polishing', reason: `Residual TSS/COD requires filtration` });
  }
  if (cod > 80) {
    train.push({ process: 'GAC Filter', purpose: 'Adsorption polishing', reason: `COD ${Math.round(cod)} mg/L — carbon adsorption` });
  }
  train.push({ process: 'Sludge Holding', purpose: 'Thickening & storage', reason: 'Waste sludge from all stages' });

  return train;
}

/* ─── Main Component ─── */
export default function AddSampleDialog({
  open,
  customers,
  onClose,
  onSave,
}: {
  open: boolean;
  customers: { id: number; name: string; company?: string | null }[];
  onClose: () => void;
  onSave: (sample: {
    customerId: number;
    sampleName: string;
    params: Record<string, number>;
    standard: string;
    notes: string;
    assess: boolean;
  }) => void;
}) {
  const { activeStandard } = useSession();
  const activeStdInfo = getStandard(activeStandard);

  const [form, setForm] = useState<NewSampleForm>({
    customerId: '', sampleName: '', flowRate: 250, bod: 1850, cod: 3200, tss: 450, ph: 6.8,
    nh3: 85, og: 12, lead: 0.3, chromium: 0.1, tds: 1200, conductivity: 1500, temperature: 25,
    notes: '', standard: activeStandard, assessAfterSave: false,
  });
  const [activeProfile, setActiveProfile] = useState(0);

  // Keep standard synced with active standard when dialog opens
  useEffect(() => {
    if (open) {
      setForm(f => ({ ...f, standard: activeStandard }));
    }
  }, [open, activeStandard]);

  const loadProfile = (idx: number) => {
    if (idx === -1) return; // Custom — do nothing
    const prof = PROFILES[idx];
    setForm(f => ({ ...f, ...prof.defaults }));
    setActiveProfile(idx);
  };

  const update = (key: keyof NewSampleForm, val: number | string | boolean) => {
    setForm(f => ({ ...f, [key]: val }));
  };

  const handleSave = () => {
    if (!form.customerId) { toast.error('Select a customer'); return; }
    if (!form.sampleName.trim()) { toast.error('Enter a sample name'); return; }

    const params: Record<string, number> = {
      flowRate: form.flowRate,
      bod: form.bod,
      cod: form.cod,
      tss: form.tss,
      ph: form.ph,
      nh3: form.nh3,
      oandg: form.og,
      lead: form.lead,
      chromium: form.chromium,
      tds: form.tds,
      conductivity: form.conductivity,
      temperature: form.temperature,
    };

    onSave({
      customerId: parseInt(form.customerId),
      sampleName: form.sampleName.trim(),
      params,
      standard: form.standard,
      notes: form.notes,
      assess: form.assessAfterSave,
    });

    // Reset
    setForm({
      customerId: '', sampleName: '', flowRate: 250, bod: 1850, cod: 3200, tss: 450, ph: 6.8,
      nh3: 85, og: 12, lead: 0.3, chromium: 0.1, tds: 1200, conductivity: 1500, temperature: 25,
      notes: '', standard: activeStandard, assessAfterSave: false,
    });
    setActiveProfile(0);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[50] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 20 }}
        className="w-full max-w-4xl rounded-2xl border border-slate-700 overflow-hidden flex flex-col max-h-[92vh]"
        style={{ background: 'var(--rb-elevated)' }}
      >
        {/* HEADER */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">Add Influent Sample</h3>
              <p className="text-xs text-slate-500">Enter water quality parameters for treatment assessment</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* BODY */}
        <div className="flex-1 overflow-auto p-5 space-y-5">
          {/* Active Standard Banner */}
          <div className="p-3 rounded-xl border border-yellow-500/30 bg-yellow-500/5 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-yellow-500/15 flex items-center justify-center shrink-0 mt-0.5">
              <span className="text-sm">{activeStdInfo.flag}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-bold text-yellow-400">{activeStdInfo.name}</span>
                <span className="text-[10px] text-yellow-400/60 bg-yellow-500/10 border border-yellow-500/20 rounded-full px-1.5 py-0.5">Default</span>
              </div>
              <p className="text-[10px] text-yellow-400/70 leading-relaxed">{activeStdInfo.description}</p>
              <p className="text-[10px] text-slate-500 mt-1">Override below if your customer requires a different standard.</p>
            </div>
          </div>

          {/* Waste Type Quick Select */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-500 uppercase tracking-wider font-bold">Quick Select</span>
            {WASTE_TYPES.map((wt, i) => (
              <button
                key={wt.label}
                onClick={() => loadProfile(wt.profileIndex)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                  (wt.profileIndex === -1 && activeProfile === -1) || (wt.profileIndex === activeProfile && wt.profileIndex !== -1)
                    ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                    : 'border-slate-700 text-slate-500 hover:text-slate-300 hover:bg-slate-800/50'
                }`}
              >
                {wt.label}
              </button>
            ))}
          </div>

          {/* Customer + Name */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Customer *</label>
              <select
                value={form.customerId}
                onChange={e => update('customerId', e.target.value)}
                className="w-full px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400 rb-card"
              >
                <option value="">Select customer...</option>
                {customers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ''}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Sample Name *</label>
              <input
                value={form.sampleName}
                onChange={e => update('sampleName', e.target.value)}
                placeholder="e.g. Raw Effluent — Week 1, Influent Grab Sample"
                className="w-full px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400 rb-card"
              />
            </div>
          </div>

          {/* Parameter Sliders */}
          <div className="space-y-1">
            <h4 className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
              <Gauge className="w-3.5 h-3.5" /> Parameters
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PARAM_CONFIG.map(p => {
                const Icon = p.icon;
                const val = (form as any)[p.key] as number;
                return (
                  <div key={p.key} className="p-3 space-y-2 rb-card">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4" style={{ color: p.color }} />
                        <span className="text-xs font-medium text-slate-300">{p.label}</span>
                        <div className="group relative">
                          <Info className="w-3 h-3 text-slate-600 cursor-help" />
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-40 p-2 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 rb-card-sm">
                            {p.desc}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          step={p.step}
                          min={p.min}
                          max={p.max}
                          value={val}
                          onChange={e => update(p.key as keyof NewSampleForm, parseFloat(e.target.value) || 0)}
                          className="w-20 px-2 py-1 rounded-lg border border-slate-700 bg-slate-900/50 text-white text-xs text-right focus:outline-none focus:ring-1 focus:ring-cyan-400/30"
                        />
                        <span className="text-[10px] text-slate-500 w-12">{p.unit}</span>
                      </div>
                    </div>
                    <input
                      type="range"
                      min={p.min}
                      max={p.max}
                      step={p.step}
                      value={val}
                      onChange={e => update(p.key as keyof NewSampleForm, parseFloat(e.target.value))}
                      className="w-full h-1.5 bg-slate-800 rounded-lg appearance-none cursor-pointer"
                      style={{ accentColor: p.color }}
                    />
                    <div className="flex justify-between text-[10px] text-slate-600">
                      <span>{p.min}</span>
                      <span>{p.max}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Assess Toggle */}
          <div className="p-4 flex items-center justify-between rb-card">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${form.assessAfterSave ? 'bg-emerald-500/15' : 'bg-slate-800'}`}>
                <CheckCircle2 className={`w-4 h-4 ${form.assessAfterSave ? 'text-emerald-400' : 'text-slate-500'}`} />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-200">Assess this sample after saving?</p>
                <p className="text-[10px] text-slate-500">Generate treatment recommendations and highlight equipment</p>
              </div>
            </div>
            <button
              onClick={() => update('assessAfterSave', !form.assessAfterSave)}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.assessAfterSave ? 'bg-emerald-500' : 'bg-slate-700'}`}
            >
              <motion.div
                animate={{ x: form.assessAfterSave ? 22 : 2 }}
                className="absolute top-1 w-4 h-4 rounded-full bg-rb-surface shadow"
              />
            </button>
          </div>

          {/* Standard + Notes */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Standard</label>
              <select
                value={form.standard}
                onChange={e => update('standard', e.target.value)}
                className="w-full px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30 rb-card"
              >
                <option value="UK_EA">🇬🇧 UK EA — Environment Agency</option>
                <option value="AU_EPA">🇦🇺 AU EPA — Environment Protection</option>
                <option value="EU_UWWTD">🇪🇺 EU UWWTD — Urban Wastewater</option>
                <option value="US_EPA">🇺🇸 US EPA — NPDES (Clean Water Act)</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Notes</label>
              <input
                value={form.notes}
                onChange={e => update('notes', e.target.value)}
                placeholder="Optional notes about sampling conditions, weather, upstream processes..."
                className="w-full px-3 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-400/30 focus:border-cyan-400 rb-card"
              />
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="p-5 border-t border-slate-700 flex items-center justify-between shrink-0 bg-slate-900/30">
          <button
            onClick={() => {
              setForm({
                customerId: '', sampleName: '', flowRate: 250, bod: 1850, cod: 3200, tss: 450, ph: 6.8,
                nh3: 85, og: 12, lead: 0.3, chromium: 0.1, tds: 1200, conductivity: 1500, temperature: 25,
                notes: '', standard: activeStandard, assessAfterSave: false,
              });
              setActiveProfile(0);
            }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <RotateCcw className="w-4 h-4" /> Reset
          </button>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!form.customerId || !form.sampleName.trim()}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-cyan-500 to-blue-500 text-white hover:from-cyan-400 hover:to-blue-400 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-md"
            >
              <Save className="w-4 h-4" /> Save Sample
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
